/**
 * useMediaState
 *
 * A media's server `state` (bkfc-cms media.md, "state -- the button"): the client renders it and
 * never derives one from the clock. Two sources keep it fresh, and the newer answer wins over the
 * `state` the item was rendered with:
 *
 * - one shared `GET /v1/live` poll, everything observed live or inside its scheduled window,
 *   rescheduled from the response's own top-level `poll_after_s`;
 * - `GET /v1/live?ids=` for a media that is not in that set: when a media leaves it, when an item
 *   arrives as LIVE / STARTING_SOON but is not in it, on the countdown's zero, and on the item's
 *   own `liveness.poll_after_s` (null means stop). Requests in the same tick go out as one batch.
 *
 * Polling runs only while something is subscribed.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { api, unwrap, retryDelayMs } from '../api';

/** The fields a fresher answer replaces on the item. */
const FRESH = ['state', 'starts_at', 'ends_at', 'watch_on', 'liveness'];
const MAX_IDS = 50; // the server's ids ceiling

const listeners = new Set();
let store = { loaded: false, live: new Map(), fetched: new Map() };
let timer = null;
let failures = 0;
let seq = 0;
const pending = new Set();
let flushTimer = null;

const emit = () => listeners.forEach(l => l());

/** By id and by slug, each entry stamped so the newest of two sources wins. */
function index(items, into = new Map()) {
  seq += 1;
  items.forEach(item => {
    const entry = { item, at: seq };
    into.set(item.id, entry);
    if (item.slug) {
      into.set(item.slug, entry);
    }
  });
  return into;
}

function schedule(ms) {
  clearTimeout(timer);
  timer = listeners.size ? setTimeout(poll, ms) : null;
}

export async function poll() {
  clearTimeout(timer);
  try {
    const { data } = await unwrap(api.GET('/v1/live'));
    const live = index(data.items);
    // A media that left the live set has a new state the shared poll no longer reports: ask for
    // it, holding its last answer until that one lands.
    const fetched = new Map(store.fetched);
    store.live.forEach((entry, key) => {
      if (!live.has(entry.item.id)) {
        fetched.set(key, entry);
        request(entry.item.id);
      }
    });
    store = { loaded: true, live, fetched };
    failures = 0;
    emit();
    schedule(data.poll_after_s * 1000);
  } catch (error) {
    console.error('[useMediaState] GET /v1/live failed:', error);
    // Keep the last answer: a failed poll is not a verdict. A rate limit waits its retry_after_s.
    schedule(retryDelayMs(failures++, error));
  }
}

async function flush() {
  flushTimer = null;
  const ids = [...pending].slice(0, MAX_IDS);
  ids.forEach(id => pending.delete(id));
  if (pending.size) {
    flushTimer = setTimeout(flush, 0);
  }
  try {
    const { data } = await unwrap(
      api.GET('/v1/live', { params: { query: { ids: ids.join(',') } } }),
    );
    store = { ...store, fetched: index(data.items, new Map(store.fetched)) };
    emit();
  } catch (error) {
    // NOT_FOUND: none of them exist any more. Anything else keeps the last state.
    if (error?.code !== 'NOT_FOUND') {
      console.error('[useMediaState] GET /v1/live?ids failed:', error);
    }
  }
}

/** Ask for one media's current state; requests in the same tick share one call. */
export function request(key) {
  if (!key) {
    return;
  }
  pending.add(key);
  if (!flushTimer) {
    flushTimer = setTimeout(flush, 0);
  }
}

function subscribe(listener) {
  listeners.add(listener);
  if (listeners.size === 1) {
    poll();
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

/** Test seam: forget the shared state between tests. */
export function resetMediaStateStore() {
  clearTimeout(timer);
  clearTimeout(flushTimer);
  timer = null;
  flushTimer = null;
  failures = 0;
  pending.clear();
  listeners.clear();
  store = { loaded: false, live: new Map(), fetched: new Map() };
}

/**
 * @param {object | null | undefined} item a card (`cardToChannel`), an event media ref or a media
 *   body, carrying the server's `state`, `starts_at`, `watch_on` and optionally `liveness`
 * @returns {object} the item with its freshest `state`, `starts_at`, `ends_at`, `watch_on` and
 *   `liveness`, plus `refresh()`
 */
export function useMediaState(item) {
  const key = item?.slug || item?.id || item?._id;
  const snap = useSyncExternalStore(subscribe, () => store);
  const inLive = key ? snap.live.get(key) : undefined;
  const fetched = key ? snap.fetched.get(key) : undefined;
  const newest = [inLive, fetched].filter(Boolean).sort((a, b) => b.at - a.at)[0];
  const current = { ...item };
  if (newest) {
    FRESH.forEach(field => {
      current[field] = newest.item[field] ?? null;
    });
  }

  // Rendered LIVE / STARTING SOON but not in the live set: that answer is stale, ask for this one.
  const staleOnArrival =
    snap.loaded && !newest && (item?.state === 'LIVE' || item?.state === 'STARTING_SOON');
  useEffect(() => {
    if (staleOnArrival) {
      request(key);
    }
  }, [staleOnArrival, key]);

  // Outside the shared set, the item's own poll_after_s says when to ask again; null is stop.
  const pollAfterS = inLive ? null : current.liveness?.poll_after_s;
  useEffect(() => {
    if (pollAfterS == null) {
      return undefined;
    }
    const t = setTimeout(() => request(key), pollAfterS * 1000);
    return () => clearTimeout(t);
  }, [pollAfterS, key, fetched]);

  const refresh = useCallback(() => {
    poll();
    request(key);
  }, [key]);

  return { ...current, refresh };
}

export default useMediaState;
